/**
 * The lead pipeline (S-A20, list view).
 *
 * !! THE LEADS ENDPOINTS DO NOT EXIST YET !! This screen runs against the MSW
 * handlers in `packages/api/src/mocks`. The shapes come from `domains/crm/models.py`,
 * so the wiring should hold, but treat the filter names below as PROVISIONAL until
 * the CRM agent reports its `get_queryset` allowlist — an unlisted filter is a 400,
 * not a silent ignore.
 *
 * One thing on this screen is a product rule rather than a layout choice: an
 * unassigned lead with no first response is the most expensive failure in this
 * product, so it is called out rather than left as an empty cell.
 */

import { qk, usePaginatedList, type Lead } from '@legalcrm/api';
import {
  Badge,
  Button,
  Input,
  Select,
  Table,
  type Column,
  type SortState,
} from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { STAFF } from '@legalcrm/api';
import { Can } from '@/auth/permissions';
import { ImportCsvDialog } from '@/components/ImportCsvDialog';
import { formatMoney, formatRelative, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';
import { useAuth } from '@/auth/AuthProvider';

const PRIORITY_OPTIONS = [
  { value: '', label: 'Any priority' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const ASSIGNMENT_OPTIONS = [
  { value: '', label: 'Anyone' },
  { value: 'me', label: 'Mine' },
  { value: 'unassigned', label: 'Unassigned' },
];

export function LeadListPage() {
  const navigate = useNavigate();
  const { baseCurrency } = useAuth();
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [priority, setPriority] = useState('');
  const [assignment, setAssignment] = useState('');
  const [sort, setSort] = useState<SortState>({ field: 'created_at', direction: 'desc' });

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const list = usePaginatedList<Lead>(qk.list('leads'), STAFF.leads, {
    search: debounced || undefined,
    priority: priority || undefined,
    assigned_user: assignment || undefined,
    ordering: `${sort.direction === 'desc' ? '-' : ''}${sort.field}`,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [debounced, priority, assignment, sort, resetCursor]);

  const columns: Column<Lead>[] = [
    {
      key: 'reference',
      header: 'Reference',
      width: 'w-36',
      render: (row) => (
        <Link
          to={`/leads/${row.id}`}
          onClick={(event) => event.stopPropagation()}
          className="font-medium text-primary hover:underline"
        >
          {row.reference}
        </Link>
      ),
    },
    {
      key: 'full_name',
      header: 'Enquirer',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink">{row.full_name}</p>
          <p className="truncate text-xs text-ink-muted">
            {row.phone || row.email || 'No contact details'}
          </p>
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Stage',
      hideOnMobile: true,
      // The firm's configured stage name. Reporting keys on `stage_type`, never on
      // the name (BR-44) — so do not derive behaviour from this string.
      render: (row) => <Badge tone="info">{row.stage.name}</Badge>,
    },
    {
      key: 'source',
      header: 'Source',
      hideOnMobile: true,
      render: (row) => <span className="text-ink-muted">{row.source?.name ?? '—'}</span>,
    },
    {
      key: 'assigned_user',
      header: 'Owner',
      render: (row) =>
        row.assigned_user ? (
          <span className="text-ink">{row.assigned_user.full_name}</span>
        ) : (
          // An unowned lead reaches the unassigned queue and is the single most
          // expensive failure in this product. Never render it as a blank cell.
          <Badge tone="danger" size="sm">
            Unassigned
          </Badge>
        ),
    },
    {
      key: 'first_response_at',
      header: 'First response',
      hideOnMobile: true,
      render: (row) =>
        row.first_response_at ? (
          <span className="text-ink-muted">{formatRelative(row.first_response_at)}</span>
        ) : (
          // Response time is a headline metric and cannot be reconstructed later,
          // which is why the backend captures it at the moment it happens.
          <Badge tone="warning" size="sm">
            Not contacted
          </Badge>
        ),
    },
    {
      key: 'expected_value',
      header: 'Value',
      align: 'right',
      hideOnMobile: true,
      render: (row) => (
        <span className="tabular-nums">
          {formatMoney(row.expected_value, row.currency || baseCurrency)}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      hideOnMobile: true,
      render: (row) => (
        <Badge tone={row.priority === 'high' ? 'warning' : 'neutral'} size="sm">
          {humanise(row.priority)}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Leads"
        description="Enquiries before they become clients."
        actions={
          <Can module="lead" action="create">
            <div className="flex gap-2">
              {/* Importing is creating, in bulk, so it sits behind the same capability
                  and beside the same button. A firm's first act on a new system is
                  bringing its pipeline across, and hiding that in settings means
                  nobody finds it. */}
              <Button variant="secondary" onClick={() => setImporting(true)}>
                Import
              </Button>
              <Button variant="primary" onClick={() => navigate('/leads/new')}>
                Quick add
              </Button>
            </div>
          </Can>
        }
      />

      <ImportCsvDialog
        kind="leads"
        open={importing}
        onClose={() => setImporting(false)}
      />

      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <Input
          label="Search"
          hideLabel
          type="search"
          placeholder="Search name, phone or reference"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select
          label="Priority"
          hideLabel
          options={PRIORITY_OPTIONS}
          value={priority}
          onChange={(event) => setPriority(event.target.value)}
        />
        <Select
          label="Owner"
          hideLabel
          options={ASSIGNMENT_OPTIONS}
          value={assignment}
          onChange={(event) => setAssignment(event.target.value)}
        />
      </div>

      <Table
        caption="Lead list"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(row) => navigate(`/leads/${row.id}`)}
        emptyTitle="No leads to show"
        emptyDescription="Nothing matches these filters."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />
    </>
  );
}
